import * as THREE from 'three';
import { GameLoop, SIM_DT } from './core/loop';
import { Tuning } from './core/tuning';
import { Terrain } from './world/terrain';
import { TerrainMesh } from './render/terrainMesh';
import { createTerrainMaterial } from './render/terrainMaterial';
import { Sky } from './render/sky';
import { BikePhysics } from './bike/physics';
import { BikeModel } from './render/bikeModel';
import { ChaseCamera } from './camera/chase';
import { InputManager, ControlInput } from './input/controls';
import { Hud } from './ui/hud';
import {
  initSystems, fixedUpdateSystems, updateSystems, renderHook, systemTimings,
  type SystemContext,
} from './core/systems';
// Importing the manifest is what registers every subsystem.
import './systems';

const app = document.getElementById('app')!;

const renderer = new THREE.WebGLRenderer({
  antialias: true,
  powerPreference: 'high-performance',
  // Needed so the capture harness can read pixels after a frame.
  preserveDrawingBuffer: true,
});
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = Tuning.render.exposure;
renderer.outputColorSpace = THREE.SRGBColorSpace;
app.appendChild(renderer.domElement);

const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(
  Tuning.camera.fovBase,
  window.innerWidth / window.innerHeight,
  0.1,
  Tuning.world.viewDistance,
);

const terrain = new Terrain(Tuning.world.seed);
const terrainMat = createTerrainMaterial();
const terrainMesh = new TerrainMesh(terrain, terrainMat.material);
scene.add(terrainMesh.group);

const sky = new Sky(scene);

const physics = new BikePhysics(terrain);
const bikeModel = new BikeModel();
scene.add(bikeModel.root);

const chase = new ChaseCamera(camera, terrain);
const input = new InputManager();
input.attach();
const hud = new Hud(app);

// Spawn on a rideable gradient rather than wherever the origin lands.
function findSpawn(): { x: number; z: number; yaw: number } {
  let best = { x: 0, z: 0, yaw: 0, score: -Infinity };
  for (let i = 0; i < 400; i++) {
    const a = (i / 400) * Math.PI * 2 * 7;
    const r = 60 + i * 2.4;
    const x = Math.cos(a) * r;
    const z = Math.sin(a) * r;
    const h = terrain.height(x, z);
    const ahead = terrain.height(x + Math.cos(a) * 12, z + Math.sin(a) * 12);
    const grade = (h - ahead) / 12;
    // Want a consistent, rideable descent: roughly 12-24%.
    const score = -Math.abs(grade - 0.18) * 10 + h / 400;
    if (score > best.score) best = { x, z, yaw: a, score };
  }
  // Face the fall line, not the search spiral's tangent. `a` is an artifact of how the
  // candidate was found and has nothing to do with the local gradient — on a drained
  // massif with ribs and gullies a fixed bearing crosses them, and the R3 spawn rolled
  // 25 m into a hollow and stopped. Terrain.descent() is the exact analytic
  // steepest-descent direction; forward is (cos yaw, sin yaw), so atan2(dz, dx).
  const fall = { x: 0, z: 0 };
  terrain.descent(best.x, best.z, fall);
  best.yaw = Math.atan2(fall.z, fall.x);
  return best;
}
const spawn = findSpawn();
physics.reset(spawn.x, spawn.z, spawn.yaw);

let lastInput: ControlInput;

const ctx: SystemContext = {
  scene,
  camera,
  renderer,
  terrain,
  physics,
  bike: physics.state,
  events: physics.events,
  dom: app,
  time: 0,
};

const loop = new GameLoop({
  fixedUpdate(dt, simTime) {
    lastInput = input.update(dt, simTime);
    if (lastInput.resetPressed) physics.reset(spawn.x, spawn.z, spawn.yaw);
    physics.step(dt, lastInput);
    ctx.time = simTime;
    fixedUpdateSystems(dt, ctx, lastInput);
  },
  render(alpha, frameDt) {
    const s = physics.state;
    // Camera shake reads the landing, it does not consume it. `landed` used to be
    // cleared here, at the top — which made it the one sim event with a different
    // lifetime from its four siblings below: anything reading it from a system's
    // update() (rather than fixedUpdate()) saw -1 forever, while bottomOut, hopped,
    // crashed and scrubImpulse all survived to the end of the frame. The comment at the
    // bottom of this function claimed all five behaved the same, and it was wrong about
    // this one. Cleared with the others now, so the rule is the rule.
    if (physics.events.landed >= 0) chase.addShake(physics.events.landed * 0.8);
    terrainMesh.update(s.x, s.z);
    terrainMat.update(frameDt, camera, loop.stats.simTime);
    sky.update(scene, s.x, s.y, s.z);
    bikeModel.update(s, alpha);
    chase.update(frameDt, s);
    updateSystems(frameDt, ctx, alpha);
    // A post-processing system may take over presentation; if none did, draw directly.
    if (!renderHook.taken) renderer.render(scene, camera);
    hud.update(s, loop.stats);
    // Systems consume sim events after the frame that produced them.
    physics.events.landed = -1;
    physics.events.bottomOut = 0;
    physics.events.hopped = false;
    physics.events.crashed = false;
    physics.events.scrubImpulse = 0;
  },
});


window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
});

await initSystems(ctx);
loop.start();

// ---------------------------------------------------------------------------
// Harness hook. The headless capture tool drives the game through this so runs are
// deterministic and independent of wall-clock timing.
declare global {
  interface Window {
    __grip: {
      loop: GameLoop;
      physics: BikePhysics;
      terrain: Terrain;
      input: InputManager;
      renderer: THREE.WebGLRenderer;
      scene: THREE.Scene;
      camera: THREE.PerspectiveCamera;
      ready: boolean;
      stepFrames(count: number, dt: number): void;
      telemetry(): Record<string, number | boolean>;
    };
  }
}

window.__grip = {
  loop,
  physics,
  terrain,
  input,
  renderer,
  scene,
  camera,
  ready: true,
  stepFrames(count: number, dt: number) {
    loop.stop();
    for (let i = 0; i < count; i++) loop.stepManual(dt);
  },
  telemetry() {
    const s = physics.state;
    return {
      simTime: loop.stats.simTime,
      fps: loop.stats.fps,
      frameMs: loop.stats.frameMs,
      simMs: loop.stats.simMs,
      renderMs: loop.stats.renderMs,
      x: s.x, y: s.y, z: s.z,
      speed: s.speed,
      speedKmh: s.speed * 3.6,
      lean: s.lean,
      leanCommand: s.leanCommand,
      pitch: s.pitch,
      yaw: s.yaw,
      steer: s.steer,
      airborne: s.airborne,
      airTime: s.airTime,
      crashed: s.crashed,
      frontTravel: s.front.travel,
      rearTravel: s.rear.travel,
      // Stroke demanded past full travel. `frontTravel` saturates at travelMax, so it
      // cannot distinguish a loaded rider-stage absorption from an unloaded stall on the
      // bump stop; these two can, read together with the loads below.
      frontOverTravel: s.front.overTravel,
      rearOverTravel: s.rear.overTravel,
      frontLoad: s.front.load,
      rearLoad: s.rear.load,
      frontSlip: s.front.slipUsage,
      rearSlip: s.rear.slipUsage,
      gForce: s.gForce,
      drawCalls: renderer.info.render.calls,
      triangles: renderer.info.render.triangles,
      // Per-system update cost, so per-system frame budgets are checkable from a capture
      // rather than inferred from whole-frame p95.
      vfxMs: systemTimings.particles ?? 0,
      tracksMs: systemTimings.tracks ?? 0,
      scatterMs: systemTimings.scatter ?? 0,
      postMs: systemTimings.post ?? 0,
      // The post system owns the app's only renderer.render(scene) call, so `postMs`
      // contains the whole world's draw submission. This is that part alone; the post
      // chain's own cost is postMs - postSceneMs.
      postSceneMs: systemTimings['post.scene'] ?? 0,
      trailsMs: systemTimings.trails ?? 0,
    };
  },
};
